import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faRedo, faMedal } from '@fortawesome/free-solid-svg-icons';
import { Role } from '../../components/auth-context/role';
import api from "../../api/api";

export function RankManager() {
    const [ranks, setRanks] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editedRanks, setEditedRanks] = useState({});

    useEffect(() => {
        fetchRanks();
    }, []);

    const fetchRanks = async () => {
        try {
            const response = await api.get('/ranks');
            if (response?.data) {
                // Asegurar que tenemos exactamente 5 rangos
                const sortedRanks = response.data.sort((a, b) => a.level - b.level);
                setRanks(sortedRanks);
                // Inicializar estado de edición
                const initialEdited = {};
                sortedRanks.forEach(rank => {
                    initialEdited[rank.level] = {
                        name: rank.name,
                        hours: rank.hours,
                        flights: rank.flights || 0
                    };
                });
                setEditedRanks(initialEdited);
            }
            setError(null);
        } catch (error) {
            console.error('Failed to fetch ranks:', error);
            setError('Failed to fetch ranks');
        }
    };

    const handleInputChange = (level, field, value) => {
        setEditedRanks(prev => ({
            ...prev,
            [level]: {
                ...prev[level],
                [field]: field === 'name' ? value : parseInt(value) || 0
            }
        }));
    };

    const handleSaveRank = async (level) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const rankData = editedRanks[level];
            await api.patch(`/ranks/level/${level}`, rankData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });

            setSuccess(`Rango nivel ${level} actualizado correctamente`);
            setTimeout(() => setSuccess(null), 3000);
            await fetchRanks();
        } catch (error) {
            console.error('Failed to update rank:', error);
            setError(error.response?.data?.error || 'Failed to update rank');
        } finally {
            setLoading(false);
        }
    };

    const handleInitializeRanks = async () => {
        if (!window.confirm('¿Estás seguro de que quieres inicializar los rangos? Esto reemplazará cualquier configuración existente.')) {
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await api.post('/ranks/initialize', {}, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });

            setSuccess('Rangos inicializados correctamente');
            setTimeout(() => setSuccess(null), 3000);
            await fetchRanks();
        } catch (error) {
            console.error('Failed to initialize ranks:', error);
            setError(error.response?.data?.error || 'Failed to initialize ranks');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Role roles={['USER_MANAGER']}>
            <div className="flex h-full" style={{ height: 'calc(100% - 53px)', background: 'var(--bg-cards)', overflow: 'auto' }}>
                <div className="manage-container">
                    <div className="manage-header">
                        <div className="title-header">
                            <FontAwesomeIcon icon={faMedal} />
                            <h1>Gestión de Rangos (Sistema de 5 Niveles)</h1>
                        </div>
                        <div>
                            {ranks.length !== 5 && (
                                <button
                                    className="btn-manage"
                                    onClick={handleInitializeRanks}
                                    disabled={loading}
                                >
                                    <FontAwesomeIcon icon={faRedo} />
                                    <span>Inicializar Rangos</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="alert alert-error" style={{ margin: '10px 0' }}>
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="alert alert-success" style={{ margin: '10px 0' }}>
                            {success}
                        </div>
                    )}

                    <div className="ranks-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '20px',
                        padding: '20px'
                    }}>
                        {ranks.map((rank) => (
                            <div key={rank.id} className="rank-card" style={{
                                background: 'var(--bg-primary)',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '8px',
                                padding: '20px'
                            }}>
                                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                    <img
                                        src={rank.img || `/resources/ranks/Rank${rank.level}.svg`}
                                        alt={rank.name}
                                        style={{
                                            height: '80px',
                                            width: 'auto',
                                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                                        }}
                                    />
                                    <h3 style={{ marginTop: '10px', color: 'var(--accent-primary)' }}>
                                        Nivel {rank.level}
                                    </h3>
                                </div>

                                <div className="form-group">
                                    <label>Nombre del Rango</label>
                                    <input
                                        type="text"
                                        value={editedRanks[rank.level]?.name || ''}
                                        onChange={(e) => handleInputChange(rank.level, 'name', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            background: 'var(--bg-secondary)',
                                            border: '1px solid var(--border-secondary)',
                                            borderRadius: '4px',
                                            color: 'var(--text-primary)'
                                        }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginTop: '10px' }}>
                                    <label>Horas Mínimas</label>
                                    <input
                                        type="number"
                                        value={editedRanks[rank.level]?.hours || 0}
                                        onChange={(e) => handleInputChange(rank.level, 'hours', e.target.value)}
                                        min="0"
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            background: 'var(--bg-secondary)',
                                            border: '1px solid var(--border-secondary)',
                                            borderRadius: '4px',
                                            color: 'var(--text-primary)'
                                        }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginTop: '10px' }}>
                                    <label>Vuelos Mínimos</label>
                                    <input
                                        type="number"
                                        value={editedRanks[rank.level]?.flights || 0}
                                        onChange={(e) => handleInputChange(rank.level, 'flights', e.target.value)}
                                        min="0"
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            background: 'var(--bg-secondary)',
                                            border: '1px solid var(--border-secondary)',
                                            borderRadius: '4px',
                                            color: 'var(--text-primary)'
                                        }}
                                    />
                                </div>

                                <button
                                    className="btn-manage"
                                    onClick={() => handleSaveRank(rank.level)}
                                    disabled={loading}
                                    style={{
                                        width: '100%',
                                        marginTop: '15px',
                                        background: 'var(--accent-primary)',
                                        color: 'white',
                                        padding: '10px',
                                        borderRadius: '4px',
                                        border: 'none',
                                        cursor: loading ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faSave} />
                                    <span style={{ marginLeft: '8px' }}>Guardar Cambios</span>
                                </button>
                            </div>
                        ))}
                    </div>

                    {ranks.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <p>No hay rangos configurados. Haz clic en "Inicializar Rangos" para crear los 5 rangos por defecto.</p>
                        </div>
                    )}
                </div>
            </div>
        </Role>
    );
}